'use client';

import * as React from 'react';

/**
 * Tracks whether the viewport has scrolled past an 80px top threshold using
 * {@link IntersectionObserver} (no scroll listeners — see web performance rules).
 *
 * Mount an invisible sentinel directly above sticky apex chrome: an in-flow
 * block with `h-20` + `-mb-20` collapses layout while preserving scroll
 * intersection semantics (when the sentinel leaves the viewport from the top,
 * we've scrolled past ~80px).
 *
 * @returns `isScrolled` starts `false` for SSR/hydration safety; updates after mount.
 */
export function useScrolledPast(): {
  isScrolled: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
} {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry?.isIntersecting);
      },
      { root: null, threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { isScrolled, sentinelRef };
}
